/*
  PEACE OUT product list: VIEW MORE, end of collection, and restore on browser "back".

  Two kinds of list use this script:
  1. Category pages ([data-po-cat], sections/po-category-grid.liquid)
     Shopify returns at most 250 products per page, so the server filters each
     250-product page to one category. Page 1 is in the HTML. The remaining 250-product
     pages are fetched in order in the background after the first paint (needed for the
     complete list, the correct order and the end-of-collection state). VIEW MORE shows
     the next 36 already-fetched products, and waits for the next page only if needed.
  2. Standard collection pages ([data-po-paged], main-collection-product-grid)
     Shopify paginates 36 per page. VIEW MORE fetches the next page and appends it.

  Back navigation: before leaving, the number of shown products and the scroll position
  are saved in sessionStorage (per URL). On a "back" navigation they are restored.
  Product data is never cached across visits: lists are always fetched fresh, so price
  and stock are current.
*/
(() => {
  const STEP = 36;
  const STATE_TTL = 30 * 60 * 1000;
  const stateKey = () => `po-list:${location.pathname}${location.search}`;

  const isBackNavigation = () => {
    const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    return nav ? nav.type === 'back_forward' : false;
  };

  const readState = () => {
    try {
      const raw = sessionStorage.getItem(stateKey());
      if (!raw) return null;
      const state = JSON.parse(raw);
      return Date.now() - state.t < STATE_TTL ? state : null;
    } catch (e) {
      return null;
    }
  };

  const writeState = (state) => {
    try {
      sessionStorage.setItem(stateKey(), JSON.stringify({ ...state, t: Date.now() }));
    } catch (e) {
      /* storage unavailable: back navigation simply starts from the top */
    }
  };

  const headerOffset = () => {
    const header = document.querySelector('.section-header');
    return header ? header.getBoundingClientRect().height : 0;
  };

  const fetchDoc = async (url) => {
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return new DOMParser().parseFromString(await res.text(), 'text/html');
  };

  // Shared footer: VIEW MORE / LOADING... / error + retry / END OF COLLECTION + BACK TO TOP.
  const buildFooter = (container, onMore) => {
    const footer = document.createElement('div');
    footer.className = 'po-more';
    footer.innerHTML = `
      <button type="button" class="po-more__button" data-po-more-button hidden>VIEW MORE</button>
      <p class="po-more__error" data-po-more-error hidden>COULD NOT LOAD. <button type="button" class="po-more__retry" data-po-more-retry>TRY AGAIN</button></p>
      <div class="po-more__end" data-po-more-end hidden>
        <p class="po-more__end-label">END OF COLLECTION</p>
        <a href="#" class="po-more__top" data-po-more-top>BACK TO TOP ↑</a>
      </div>`;
    container.appendChild(footer);
    const button = footer.querySelector('[data-po-more-button]');
    const error = footer.querySelector('[data-po-more-error]');
    const end = footer.querySelector('[data-po-more-end]');
    button.addEventListener('click', onMore);
    footer.querySelector('[data-po-more-retry]').addEventListener('click', onMore);
    footer.querySelector('[data-po-more-top]').addEventListener('click', (event) => {
      event.preventDefault();
      const target = document.querySelector('.po-category-nav') || container;
      const top = target.getBoundingClientRect().top + window.scrollY - headerOffset() - 8;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    });
    return {
      set({ more, loading, failed, done }) {
        button.hidden = !(more || loading) || failed;
        button.disabled = loading;
        button.textContent = loading ? 'LOADING...' : 'VIEW MORE';
        button.setAttribute('aria-busy', loading ? 'true' : 'false');
        error.hidden = !failed;
        end.hidden = !done || loading || failed;
      },
    };
  };

  const restoreScroll = (y) => {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    requestAnimationFrame(() => window.scrollTo(0, y));
  };

  // ---------- 1. Category pages ----------
  const initCategory = (root) => {
    const list = root.querySelector('[data-po-list]');
    const status = root.querySelector('[data-po-status]');
    const empty = root.querySelector('[data-po-empty]');
    let totalPages = parseInt(root.dataset.pages, 10) || 1;
    let fetchedPages = 1;
    let shown = STEP;
    let inflight = null;
    let failed = false;
    let busy = false;

    // ARCHIVE: the registration date is day 1, in Japan time (data-po-day = that date as a day
    // number). The server already filtered; this repeats the check with the current date, because
    // the page HTML may come from a short-lived cache or the tab may stay open past midnight.
    // If the device clock is clearly wrong (more than 2 days off the server), use the server date.
    const archiveDay = parseInt(root.dataset.poArchiveDay, 10) || 0;
    const serverToday = parseInt(root.dataset.poToday, 10) || 0;
    const isArchivePage = root.dataset.poCategory === 'archive';
    const today = () => {
      const device = Math.floor((Date.now() + 9 * 3600 * 1000) / 86400000);
      return Math.abs(device - serverToday) > 2 ? serverToday : Math.max(device, serverToday);
    };
    const applyArchiveRule = (scope) => {
      if (!archiveDay) return;
      const now = today();
      scope.querySelectorAll('[data-po-item][data-po-day]').forEach((item) => {
        const archived = now - parseInt(item.dataset.poDay, 10) + 1 >= archiveDay;
        if (archived !== isArchivePage) item.remove();
      });
    };

    root.classList.add('po-cat--js');
    const items = () => Array.from(list.querySelectorAll('[data-po-item]'));
    const allFetched = () => fetchedPages >= totalPages;

    const pageUrl = (page) => {
      const url = new URL(location.href);
      url.hash = '';
      url.searchParams.set('page', page);
      return url.toString();
    };

    // Remaining 250-product pages are requested in parallel (each at most once),
    // but appended strictly in page order so the product order never changes.
    // A failed page is forgotten and requested again on retry; successful ones are kept.
    const requests = {};
    const requestPage = (page) => {
      if (!requests[page]) {
        requests[page] = fetchDoc(pageUrl(page)).catch((error) => {
          delete requests[page];
          throw error;
        });
      }
      return requests[page];
    };

    const fetchNext = () => {
      if (allFetched()) return Promise.resolve();
      if (inflight) return inflight;
      for (let page = fetchedPages + 1; page <= totalPages; page++) requestPage(page).catch(() => {});
      const page = fetchedPages + 1;
      inflight = requestPage(page)
        .then((doc) => {
          applyArchiveRule(doc);
          doc.querySelectorAll('[data-po-cat] [data-po-item]').forEach((item) => list.appendChild(item));
          delete requests[page];
          fetchedPages = page;
          // ARCHIVE sorted oldest first: this page may say that later pages have nothing to show.
          const docRoot = doc.querySelector('[data-po-cat]');
          const docPages = docRoot ? parseInt(docRoot.dataset.pages, 10) : 0;
          if (docPages && docPages < totalPages) totalPages = Math.max(docPages, page);
          failed = false;
        })
        .finally(() => {
          inflight = null;
        });
      return inflight;
    };

    const fetchAll = async () => {
      while (!allFetched()) await fetchNext();
    };

    const footer = buildFooter(root, async () => {
      if (busy) return;
      busy = true;
      failed = false;
      render(true);
      try {
        const target = shown + STEP;
        while (items().length < target && !allFetched()) await fetchNext();
        shown = target;
      } catch (e) {
        failed = true;
      }
      busy = false;
      render(false);
    });

    function render(loading) {
      const all = items();
      all.forEach((item, index) => {
        item.hidden = index >= shown;
      });
      root.classList.add('po-cat--ready');
      const known = allFetched();
      // More exists if fetched items are still hidden, or if the visible slots are full
      // and later 250-product pages are not fetched yet.
      const more = all.length > shown || (!known && all.length >= shown);
      const done = known && all.length <= shown;
      footer.set({ more, loading, failed, done: done && all.length > 0 });
      empty.hidden = !(known && all.length === 0);
      // Nothing on the first 250-product page yet (e.g. HOODIE): say it is loading, not empty.
      status.textContent = known && all.length > 0 ? `${all.length} items` : !known && all.length === 0 && !failed ? 'LOADING...' : '';
    }

    const saveState = () => writeState({ shown, y: window.scrollY });
    window.addEventListener('pagehide', saveState);
    list.addEventListener('click', (event) => {
      if (event.target.closest('a[href*="/products/"]')) saveState();
    });

    applyArchiveRule(list);
    // Re-check when the tab comes back (e.g. left open past midnight in Japan).
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && !busy) {
        applyArchiveRule(list);
        render(false);
      }
    });

    const saved = isBackNavigation() ? readState() : null;
    if (saved && saved.shown > STEP) {
      shown = saved.shown;
      busy = true;
      render(true);
      (async () => {
        try {
          while (items().length < shown && !allFetched()) await fetchNext();
        } catch (e) {
          failed = true;
        }
        busy = false;
        render(false);
        restoreScroll(saved.y);
        fetchAll().then(() => render(false)).catch(() => {});
      })();
    } else {
      render(false);
      if (saved) restoreScroll(saved.y);
      // Background: complete the list (order, count, end state). This script is deferred,
      // so it runs after the HTML is parsed and the first products are already on screen.
      const start = () => fetchAll().then(() => render(false)).catch(() => {
        failed = true;
        render(false);
      });
      setTimeout(start, 0);
    }
  };

  // ---------- 2. Standard collection pages (36 per page) ----------
  const initPaged = (root) => {
    const list = root.querySelector('#product-grid');
    if (!list) return;
    const fallback = root.querySelector('[data-po-paged-fallback]');
    if (fallback) fallback.hidden = true;
    let nextUrl = root.dataset.nextUrl || '';
    let pagesLoaded = 1;
    let busy = false;
    let failed = false;

    // NEW: products registered within N days (data-po-created = registration time in seconds).
    // The server already filtered; this repeats the check with the current time, because the
    // page HTML may come from a short-lived cache or the tab may stay open past the cutoff.
    // If the device clock is clearly wrong (more than 2 days off the server), use the server time.
    const newDays = parseInt(root.dataset.poNewDays, 10) || 0;
    const serverCutoff = parseInt(root.dataset.poNewCutoff, 10) || 0;
    const newEmpty = root.querySelector('[data-po-new-empty]');
    const newCutoff = () => {
      const clientCutoff = Math.floor(Date.now() / 1000) - newDays * 86400;
      return Math.abs(clientCutoff - serverCutoff) > 2 * 86400 ? serverCutoff : Math.max(clientCutoff, serverCutoff);
    };
    // Returns true when at least one card was older than the cutoff (list is newest first).
    const applyNewRule = () => {
      if (!newDays) return false;
      const cutoff = newCutoff();
      let removed = false;
      list.querySelectorAll('li[data-po-created]').forEach((item) => {
        if (parseInt(item.dataset.poCreated, 10) < cutoff) {
          item.remove();
          removed = true;
        }
      });
      if (newEmpty) newEmpty.hidden = list.querySelectorAll('li').length > 0;
      return removed;
    };

    const loadNext = async () => {
      const doc = await fetchDoc(nextUrl);
      const nextRoot = doc.querySelector('[data-po-paged]');
      doc.querySelectorAll('#product-grid > li').forEach((item) => {
        if (item.classList.contains('po-pinned-item')) return;
        item.classList.remove('scroll-trigger', 'animate--slide-in');
        list.appendChild(item);
      });
      nextUrl = nextRoot ? nextRoot.dataset.nextUrl || '' : '';
      pagesLoaded += 1;
      if (applyNewRule()) nextUrl = '';
    };

    const footer = buildFooter(root, async () => {
      if (busy || !nextUrl) return;
      busy = true;
      failed = false;
      footer.set({ more: true, loading: true, failed: false, done: false });
      try {
        await loadNext();
      } catch (e) {
        failed = true;
      }
      busy = false;
      render();
    });

    function render() {
      const done = !nextUrl;
      const count = list.querySelectorAll('li').length;
      footer.set({ more: !done, loading: false, failed, done: done && count > 0 });
    }

    if (applyNewRule()) nextUrl = '';
    // Re-check when the tab comes back (e.g. left open past the cutoff).
    if (newDays) {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && applyNewRule()) {
          nextUrl = '';
          render();
        }
      });
    }

    const saveState = () => writeState({ pages: pagesLoaded, y: window.scrollY });
    window.addEventListener('pagehide', saveState);
    list.addEventListener('click', (event) => {
      if (event.target.closest('a[href*="/products/"]')) saveState();
    });

    const saved = isBackNavigation() ? readState() : null;
    if (saved && saved.pages > 1 && nextUrl) {
      busy = true;
      footer.set({ more: true, loading: true, failed: false, done: false });
      (async () => {
        try {
          while (pagesLoaded < saved.pages && nextUrl) await loadNext();
        } catch (e) {
          failed = true;
        }
        busy = false;
        render();
        restoreScroll(saved.y);
      })();
    } else {
      render();
      if (saved) restoreScroll(saved.y);
    }
  };

  document.querySelectorAll('[data-po-cat]').forEach(initCategory);
  document.querySelectorAll('[data-po-paged]').forEach(initPaged);
})();
